// GPU-accelerated particle system for intense explosion effects
// Uses point sprites rendered on GPU for 10K+ particles at 60fps

interface ExplosionConfig {
  particleCount: number;
  lifetime: number;
  speed: number;
  spread: number;
  gravity: number;
  fadeStart: number;
}

const DEFAULT_EXPLOSION_CONFIG: ExplosionConfig = {
  particleCount: 5000,
  lifetime: 1.5,
  speed: 300,
  spread: 1.0,
  gravity: 50,
  fadeStart: 0.6,
};

interface ExplosionLayer {
  particleCount: number;
  colorPalette: string[];
  speed: { min: number; max: number };
  lifetime: { min: number; max: number };
  size: { min: number; max: number };
  spread: number;
  gravity: number;
  drag: number;
  fadeStart: number;
  turbulence: number;
}

interface ExplosionPreset {
  layers: ExplosionLayer[];
}

const SMALL_EXPLOSION_PRESET: ExplosionPreset = {
  layers: [
    {
      particleCount: 7,
      colorPalette: ["#FFFFFF", "#FFFF00", "#FF8800"],
      speed: { min: 150, max: 350 },
      lifetime: { min: 0.1, max: 0.15 },
      size: { min: 8, max: 16 },
      spread: 1.0,
      gravity: 0,
      drag: 0.8,
      fadeStart: 0.0,
      turbulence: 0.05,
    },
  ],
};

const MEDIUM_EXPLOSION_PRESET: ExplosionPreset = {
  layers: [
    {
      particleCount: 6,
      colorPalette: ["#FFFFFF", "#FFFF00"],
      speed: { min: 200, max: 450 },
      lifetime: { min: 0.15, max: 0.25 },
      size: { min: 10, max: 18 },
      spread: 1.0,
      gravity: 0,
      drag: 0.82,
      fadeStart: 0.0,
      turbulence: 0.08,
    },
    {
      particleCount: 16,
      colorPalette: ["#FFFF00", "#FF8800", "#FF0000"],
      speed: { min: 250, max: 550 },
      lifetime: { min: 0.25, max: 0.45 },
      size: { min: 6, max: 12 },
      spread: 1.0,
      gravity: 0,
      drag: 0.75,
      fadeStart: 0.2,
      turbulence: 0.1,
    },
  ],
};

const LARGE_EXPLOSION_PRESET: ExplosionPreset = {
  layers: [
    {
      particleCount: 12,
      colorPalette: ["#FFFFFF", "#FFFF00"],
      speed: { min: 300, max: 600 },
      lifetime: { min: 0.2, max: 0.35 },
      size: { min: 14, max: 24 },
      spread: 1.0,
      gravity: 0,
      drag: 0.78,
      fadeStart: 0.0,
      turbulence: 0.08,
    },
    {
      particleCount: 32,
      colorPalette: ["#FFFF00", "#FF8800", "#FF0000"],
      speed: { min: 350, max: 750 },
      lifetime: { min: 0.35, max: 0.65 },
      size: { min: 8, max: 14 },
      spread: 1.0,
      gravity: 0,
      drag: 0.72,
      fadeStart: 0.25,
      turbulence: 0.12,
    },
  ],
};

const EXPLOSION_PRESETS: Record<string, ExplosionPreset> = {
  small: SMALL_EXPLOSION_PRESET,
  medium: MEDIUM_EXPLOSION_PRESET,
  large: LARGE_EXPLOSION_PRESET,
};

interface Particle {
  posX: number;
  posY: number;
  velX: number;
  velY: number;
  life: number;
  maxLife: number;
  size: number;
  colorPalette: string[];
  drag: number;
  gravity: number;
  fadeStart: number;
  turbulence: number;
}

export class GPUExplosionParticles {
  private gl: WebGLRenderingContext;
  private glCanvas: HTMLCanvasElement;
  private config: ExplosionConfig;

  private particleShader: ShaderProgram;

  private particles: Particle[] = [];
  private particleBuffer: WebGLBuffer;
  private maxParticles: number;

  private canvasWidth = 0;
  private canvasHeight = 0;

  constructor(
    canvasWidth: number,
    canvasHeight: number,
    config: Partial<ExplosionConfig> = {},
  ) {
    this.config = { ...DEFAULT_EXPLOSION_CONFIG, ...config };
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.maxParticles = this.config.particleCount * 10;

    this.glCanvas = document.createElement("canvas");
    this.glCanvas.width = canvasWidth;
    this.glCanvas.height = canvasHeight;
    const gl = this.glCanvas.getContext("webgl", { alpha: true });
    if (!gl) {
      throw new Error("WebGL not supported");
    }
    this.gl = gl;

    this.particleShader = this.createParticleShader();

    this.particleBuffer = this.createParticleBuffer();

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
  }

  spawnExplosion(
    worldX: number,
    worldY: number,
    intensity: number = 1.0,
    colorPalette?: string[],
    radius: number = 80,
    preset?: "small" | "medium" | "large",
  ): void {
    this.spawnRealisticExplosion(
      worldX,
      worldY,
      intensity,
      radius,
      preset,
      colorPalette,
    );
  }

  private spawnRealisticExplosion(
    worldX: number,
    worldY: number,
    intensity: number,
    radius: number,
    presetName?: "small" | "medium" | "large",
    customColorPalette?: string[],
  ): void {
    const selectedPreset = presetName || "large";
    const preset = EXPLOSION_PRESETS[selectedPreset];
    if (!preset) return;
    const radiusScale = radius / 80;

    for (const layer of preset.layers) {
      const count = Math.floor(layer.particleCount * intensity);

      for (let i = 0; i < count; i++) {
        if (this.particles.length >= this.maxParticles) break;

        const angle = Math.random() * Math.PI * 2;
        const clusterBias = Math.random() < 0.3 ? Math.random() * 0.5 : 1.0;

        const speedRange = layer.speed.max - layer.speed.min;
        const baseSpeed = layer.speed.min + Math.random() * speedRange;
        const speed = baseSpeed * intensity * clusterBias * radiusScale;

        const lifetimeRange = layer.lifetime.max - layer.lifetime.min;
        const lifetime = layer.lifetime.min + Math.random() * lifetimeRange;

        const sizeRange = layer.size.max - layer.size.min;
        const baseSize = layer.size.min + Math.random() * sizeRange;
        const size = baseSize * Math.sqrt(radiusScale);

        const colorPalette = customColorPalette || layer.colorPalette;

        const spreadVariation = layer.spread + (Math.random() - 0.5) * 0.2;
        const chaosX = (Math.random() - 0.5) * speed * 0.3;
        const chaosY = (Math.random() - 0.5) * speed * 0.3;

        this.particles.push({
          posX: worldX,
          posY: worldY,
          velX: Math.cos(angle) * speed * spreadVariation + chaosX,
          velY: Math.sin(angle) * speed * spreadVariation + chaosY,
          life: 1.0,
          maxLife: lifetime,
          size: size,
          colorPalette: colorPalette,
          drag: layer.drag,
          gravity: layer.gravity,
          fadeStart: layer.fadeStart,
          turbulence: layer.turbulence,
        });
      }
    }
  }

  update(deltaTime: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      if (!p) continue;

      const decayRate = 1 / p.maxLife;
      p.life -= decayRate * deltaTime;

      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }

      if (p.turbulence > 0) {
        const turbulenceStrength = p.turbulence * 100;
        p.velX += (Math.random() - 0.5) * turbulenceStrength * deltaTime;
        p.velY += (Math.random() - 0.5) * turbulenceStrength * deltaTime;
      }

      p.posX += p.velX * deltaTime;
      p.posY += p.velY * deltaTime;

      p.velY += p.gravity * deltaTime;

      const dragFactor = p.drag ** (deltaTime * 60);
      p.velX *= dragFactor;
      p.velY *= dragFactor;
    }
  }

  render(
    targetCtx: CanvasRenderingContext2D,
    cameraX: number,
    cameraY: number,
  ): void {
    if (this.particles.length === 0) return;

    const gl = this.gl;

    gl.viewport(0, 0, this.canvasWidth, this.canvasHeight);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    this.activateShader(this.particleShader);

    gl.uniform2f(
      this.particleShader.uniforms.uResolution!,
      this.canvasWidth,
      this.canvasHeight,
    );
    gl.uniform2f(this.particleShader.uniforms.uCamera!, cameraX, cameraY);

    this.uploadParticleData();

    gl.drawArrays(gl.POINTS, 0, this.particles.length);

    targetCtx.globalCompositeOperation = "lighter";
    targetCtx.drawImage(this.glCanvas, 0, 0);
    targetCtx.globalCompositeOperation = "source-over";
  }

  resize(width: number, height: number): void {
    this.canvasWidth = width;
    this.canvasHeight = height;
    this.glCanvas.width = width;
    this.glCanvas.height = height;
  }

  getParticleCount(): number {
    return this.particles.length;
  }

  private interpolateColor(
    colorPalette: string[],
    life: number,
  ): { r: number; g: number; b: number } {
    if (colorPalette.length === 0) {
      return { r: 1.0, g: 1.0, b: 1.0 };
    }

    if (colorPalette.length === 1) {
      const color = colorPalette[0] || "#FFFFFF";
      return {
        r: Number.parseInt(color.slice(1, 3), 16) / 255,
        g: Number.parseInt(color.slice(3, 5), 16) / 255,
        b: Number.parseInt(color.slice(5, 7), 16) / 255,
      };
    }

    const normalizedLife = 1.0 - life;
    const palettePosition = normalizedLife * (colorPalette.length - 1);
    const index1 = Math.floor(palettePosition);
    const index2 = Math.min(index1 + 1, colorPalette.length - 1);
    const t = palettePosition - index1;

    const color1 = colorPalette[index1] || "#FFFFFF";
    const color2 = colorPalette[index2] || "#FFFFFF";

    const r1 = Number.parseInt(color1.slice(1, 3), 16) / 255;
    const g1 = Number.parseInt(color1.slice(3, 5), 16) / 255;
    const b1 = Number.parseInt(color1.slice(5, 7), 16) / 255;

    const r2 = Number.parseInt(color2.slice(1, 3), 16) / 255;
    const g2 = Number.parseInt(color2.slice(3, 5), 16) / 255;
    const b2 = Number.parseInt(color2.slice(5, 7), 16) / 255;

    return {
      r: r1 + (r2 - r1) * t,
      g: g1 + (g2 - g1) * t,
      b: b1 + (b2 - b1) * t,
    };
  }

  private createParticleShader(): ShaderProgram {
    const vertSrc = `
      attribute vec2 aPosition;
      attribute vec2 aVelocity;
      attribute float aLife;
      attribute float aSize;
      attribute vec3 aColor;
      attribute float aFadeStart;

      uniform vec2 uResolution;
      uniform vec2 uCamera;

      varying float vLife;
      varying vec3 vColor;
      varying float vFadeStart;
      varying vec2 vVelocity;
      varying float vSpeed;

      void main() {
        vec2 screenPos = aPosition - uCamera;
        screenPos += uResolution / 2.0;

        vec2 clipPos = (screenPos / uResolution) * 2.0 - 1.0;
        clipPos.y *= -1.0;

        gl_Position = vec4(clipPos, 0.0, 1.0);
        gl_PointSize = aSize * aLife;

        vLife = aLife;
        vColor = aColor;
        vFadeStart = aFadeStart;
        vVelocity = aVelocity;
        vSpeed = length(aVelocity);
      }
    `;

    const fragSrc = `
      precision mediump float;

      varying float vLife;
      varying vec3 vColor;
      varying float vFadeStart;

      // Thermal cooling gradient: hot colors fade to cooler colors
      vec3 applyCooling(vec3 initialColor, float life) {
        float heat = life;

        float coolingFactor = 0.4 + (heat * 0.6);

        vec3 cooledColor = initialColor;
        cooledColor.r = initialColor.r;
        cooledColor.g = initialColor.g * (0.3 + heat * 0.7);
        cooledColor.b = initialColor.b * (0.1 + heat * 0.9);

        cooledColor *= coolingFactor;

        return cooledColor;
      }

      void main() {
        vec2 coord = gl_PointCoord - vec2(0.5);
        float dist = length(coord);
        if (dist > 0.5) discard;

        float edgeAlpha = 1.0 - smoothstep(0.3, 0.5, dist);

        float lifeFade = 1.0;
        if (vLife < vFadeStart) {
          lifeFade = vLife / vFadeStart;
        }

        float alpha = edgeAlpha * lifeFade;

        vec3 finalColor = applyCooling(vColor, vLife);

        gl_FragColor = vec4(finalColor, alpha);
      }
    `;

    return this.compileShaderProgram(vertSrc, fragSrc);
  }

  private compileShaderProgram(
    vertSrc: string,
    fragSrc: string,
  ): ShaderProgram {
    const gl = this.gl;

    const vertShader = this.compileShader(gl.VERTEX_SHADER, vertSrc);
    const fragShader = this.compileShader(gl.FRAGMENT_SHADER, fragSrc);

    const program = gl.createProgram()!;
    gl.attachShader(program, vertShader);
    gl.attachShader(program, fragShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(`Shader link error: ${gl.getProgramInfoLog(program)}`);
    }

    const uniforms: Record<string, WebGLUniformLocation> = {};
    const uniformNames = ["uResolution", "uCamera"];

    for (const name of uniformNames) {
      const loc = gl.getUniformLocation(program, name);
      if (loc) uniforms[name] = loc;
    }

    const attributes = {
      aPosition: gl.getAttribLocation(program, "aPosition"),
      aVelocity: gl.getAttribLocation(program, "aVelocity"),
      aLife: gl.getAttribLocation(program, "aLife"),
      aSize: gl.getAttribLocation(program, "aSize"),
      aColor: gl.getAttribLocation(program, "aColor"),
      aFadeStart: gl.getAttribLocation(program, "aFadeStart"),
    };

    return { program, uniforms, attributes };
  }

  private compileShader(type: number, source: string): WebGLShader {
    const gl = this.gl;
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      throw new Error(`Shader compile error: ${gl.getShaderInfoLog(shader)}`);
    }

    return shader;
  }

  private createParticleBuffer(): WebGLBuffer {
    const gl = this.gl;
    const buffer = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.maxParticles * 10 * 4, gl.DYNAMIC_DRAW);
    return buffer;
  }

  private uploadParticleData(): void {
    const gl = this.gl;
    const data = new Float32Array(this.particles.length * 10);

    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      if (!p) continue;

      const color = this.interpolateColor(p.colorPalette, p.life);

      const idx = i * 10;
      data[idx + 0] = p.posX;
      data[idx + 1] = p.posY;
      data[idx + 2] = p.velX;
      data[idx + 3] = p.velY;
      data[idx + 4] = p.life;
      data[idx + 5] = p.size;
      data[idx + 6] = color.r;
      data[idx + 7] = color.g;
      data[idx + 8] = color.b;
      data[idx + 9] = p.fadeStart;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, this.particleBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, data);
  }

  private activateShader(shader: ShaderProgram): void {
    const gl = this.gl;
    gl.useProgram(shader.program);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.particleBuffer);

    const stride = 10 * 4;

    if (shader.attributes.aPosition >= 0) {
      gl.enableVertexAttribArray(shader.attributes.aPosition);
      gl.vertexAttribPointer(
        shader.attributes.aPosition,
        2,
        gl.FLOAT,
        false,
        stride,
        0,
      );
    }

    if (shader.attributes.aVelocity >= 0) {
      gl.enableVertexAttribArray(shader.attributes.aVelocity);
      gl.vertexAttribPointer(
        shader.attributes.aVelocity,
        2,
        gl.FLOAT,
        false,
        stride,
        2 * 4,
      );
    }

    if (shader.attributes.aLife >= 0) {
      gl.enableVertexAttribArray(shader.attributes.aLife);
      gl.vertexAttribPointer(
        shader.attributes.aLife,
        1,
        gl.FLOAT,
        false,
        stride,
        4 * 4,
      );
    }

    if (shader.attributes.aSize >= 0) {
      gl.enableVertexAttribArray(shader.attributes.aSize);
      gl.vertexAttribPointer(
        shader.attributes.aSize,
        1,
        gl.FLOAT,
        false,
        stride,
        5 * 4,
      );
    }

    if (shader.attributes.aColor >= 0) {
      gl.enableVertexAttribArray(shader.attributes.aColor);
      gl.vertexAttribPointer(
        shader.attributes.aColor,
        3,
        gl.FLOAT,
        false,
        stride,
        6 * 4,
      );
    }

    if (shader.attributes.aFadeStart >= 0) {
      gl.enableVertexAttribArray(shader.attributes.aFadeStart);
      gl.vertexAttribPointer(
        shader.attributes.aFadeStart,
        1,
        gl.FLOAT,
        false,
        stride,
        9 * 4,
      );
    }
  }
}

interface ShaderProgram {
  program: WebGLProgram;
  uniforms: Record<string, WebGLUniformLocation>;
  attributes: {
    aPosition: number;
    aVelocity: number;
    aLife: number;
    aSize: number;
    aColor: number;
    aFadeStart: number;
  };
}
